import { safeHref } from './links';
import { isVideoId, type VideoProvider } from './video';

/**
 * Zengin metin belgesi. Panel editörünün (TipTap/ProseMirror) JSON çıktısıyla uyumlu, ama yalnızca izin
 * verilen düğüm ve işaretleri içeren bir alt küme. Belge veritabanına HTML olarak değil bu yapıyla yazılır ve
 * vitrinde HTML enjeksiyonu yapmayan işleyiciyle (react.tsx) gösterilir. Bilinmeyen düğüm reddedilir,
 * bilinmeyen öznitelik atılır.
 */
export type Mark = { type: 'bold' } | { type: 'italic' } | { type: 'link'; attrs: LinkAttrs };
export interface LinkAttrs {
  href: string;
  target: '_blank' | null;
}
export type Inline = { type: 'text'; text: string; marks?: Mark[] } | { type: 'hardBreak' };
export interface Paragraph {
  type: 'paragraph';
  content?: Inline[];
}
export interface Heading {
  type: 'heading';
  attrs: { level: 2 | 3 | 4 };
  content?: Inline[];
}
export interface ListItem {
  type: 'listItem';
  content: Array<Paragraph | BulletList | OrderedList>;
}
export interface BulletList {
  type: 'bulletList';
  content: ListItem[];
}
export interface OrderedList {
  type: 'orderedList';
  attrs: { start: number };
  content: ListItem[];
}
export interface Blockquote {
  type: 'blockquote';
  content: Paragraph[];
}
export interface HorizontalRule {
  type: 'horizontalRule';
}
export interface ImageNode {
  type: 'image';
  attrs: { fileId: string; alt: string; caption: string | null };
}
/** Tablo (F5B-18): satırlar ve hücreler; hücre birleştirme yok. İlk satır başlık hücreleriyse tablo başlığıdır. */
export interface TableCell {
  type: 'tableCell' | 'tableHeader';
  content: Paragraph[];
}
export interface TableRow {
  type: 'tableRow';
  content: TableCell[];
}
export interface TableNode {
  type: 'table';
  content: TableRow[];
}
/** Gömülü video (F5B-18): yalnızca izin listesindeki sağlayıcılar; adres değil sağlayıcı + kimlik saklanır. */
export interface VideoNode {
  type: 'video';
  attrs: { provider: VideoProvider; videoId: string; title: string };
}
export type Block =
  | Paragraph
  | Heading
  | BulletList
  | OrderedList
  | Blockquote
  | HorizontalRule
  | ImageNode
  | TableNode
  | VideoNode;
export interface RichDoc {
  type: 'doc';
  content: Block[];
}

export const LIMITS = {
  /** Belgedeki toplam düğüm sayısı. */
  nodes: 5000,
  /** Toplam metin uzunluğu (karakter). */
  text: 100_000,
  /** İç içe liste derinliği. */
  listDepth: 4,
  alt: 300,
  caption: 500,
  tableRows: 100,
  tableColumns: 12,
  videoTitle: 200,
} as const;

export interface ContentIssue {
  /** Belge içindeki konum (ör. `content.3.content.0`). */
  path: Array<string | number>;
  code: ContentErrorCode;
}

export type ContentErrorCode =
  | 'content_invalid'
  | 'content_unknown_node'
  | 'content_invalid_link'
  | 'content_image_alt_required'
  | 'content_too_large'
  | 'content_too_deep'
  | 'content_video_title_required';

export class ContentError extends Error {
  constructor(readonly issues: ContentIssue[]) {
    super(issues.map((i) => `${i.path.join('.')}: ${i.code}`).join('; '));
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Metin düğümlerinde yeri olmayan kontrol karakterleri (satır sonu da: satır sonu `hardBreak` düğümüdür).
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);

class Walker {
  nodes = 0;
  text = 0;
  issues: ContentIssue[] = [];
  private stop = false;

  fail(path: Array<string | number>, code: ContentErrorCode): null {
    if (!this.stop) this.issues.push({ path, code });
    if (code === 'content_too_large') this.stop = true;
    return null;
  }

  count(path: Array<string | number>): boolean {
    this.nodes += 1;
    if (this.nodes > LIMITS.nodes) {
      this.fail(path, 'content_too_large');
      return false;
    }
    return !this.stop;
  }

  marks(raw: unknown, path: Array<string | number>): Mark[] | undefined {
    if (raw === undefined || raw === null) return undefined;
    if (!Array.isArray(raw)) return this.fail(path, 'content_invalid') ?? undefined;
    const out: Mark[] = [];
    const seen = new Set<string>();
    raw.forEach((m, i) => {
      if (!isObj(m)) return void this.fail([...path, i], 'content_invalid');
      if (seen.has(String(m.type))) return;
      if (m.type === 'bold' || m.type === 'italic') {
        out.push({ type: m.type });
        seen.add(m.type);
      } else if (m.type === 'link') {
        const attrs = isObj(m.attrs) ? m.attrs : {};
        const href = safeHref(attrs.href);
        if (!href) return void this.fail([...path, i, 'attrs', 'href'], 'content_invalid_link');
        out.push({ type: 'link', attrs: { href, target: attrs.target === '_blank' ? '_blank' : null } });
        seen.add('link');
      } else {
        this.fail([...path, i], 'content_unknown_node');
      }
    });
    return out.length ? out : undefined;
  }

  inline(raw: unknown, path: Array<string | number>): Inline[] | undefined {
    if (raw === undefined || raw === null) return undefined;
    if (!Array.isArray(raw)) return this.fail(path, 'content_invalid') ?? undefined;
    const out: Inline[] = [];
    raw.forEach((n, i) => {
      const p = [...path, i];
      if (!isObj(n)) return void this.fail(p, 'content_invalid');
      if (!this.count(p)) return;
      if (n.type === 'hardBreak') return void out.push({ type: 'hardBreak' });
      if (n.type !== 'text') return void this.fail(p, 'content_unknown_node');
      if (typeof n.text !== 'string') return void this.fail(p, 'content_invalid');
      const text = n.text.replace(/\r\n?|\n/g, ' ').replace(CONTROL, '');
      if (!text) return;
      this.text += text.length;
      if (this.text > LIMITS.text) return void this.fail(p, 'content_too_large');
      const marks = this.marks(n.marks, [...p, 'marks']);
      out.push(marks ? { type: 'text', text, marks } : { type: 'text', text });
    });
    return out.length ? out : undefined;
  }

  paragraph(n: Obj, path: Array<string | number>): Paragraph {
    const content = this.inline(n.content, [...path, 'content']);
    return content ? { type: 'paragraph', content } : { type: 'paragraph' };
  }

  listItems(raw: unknown, path: Array<string | number>, depth: number): ListItem[] {
    if (!Array.isArray(raw) || raw.length === 0) {
      this.fail(path, 'content_invalid');
      return [];
    }
    const out: ListItem[] = [];
    raw.forEach((item, i) => {
      const p = [...path, i];
      if (!isObj(item) || item.type !== 'listItem') return void this.fail(p, 'content_unknown_node');
      if (!this.count(p)) return;
      const children: ListItem['content'] = [];
      const kids = Array.isArray(item.content) ? item.content : [];
      kids.forEach((k, j) => {
        const kp = [...p, 'content', j];
        if (!isObj(k)) return void this.fail(kp, 'content_invalid');
        if (k.type === 'paragraph') {
          if (this.count(kp)) children.push(this.paragraph(k, kp));
        } else if (k.type === 'bulletList' || k.type === 'orderedList') {
          const list = this.list(k, kp, depth + 1);
          if (list) children.push(list);
        } else this.fail(kp, 'content_unknown_node');
      });
      out.push({ type: 'listItem', content: children.length ? children : [{ type: 'paragraph' }] });
    });
    return out;
  }

  list(n: Obj, path: Array<string | number>, depth: number): BulletList | OrderedList | null {
    if (depth > LIMITS.listDepth) return this.fail(path, 'content_too_deep');
    if (!this.count(path)) return null;
    const items = this.listItems(n.content, [...path, 'content'], depth);
    if (n.type === 'bulletList') return { type: 'bulletList', content: items };
    const attrs = isObj(n.attrs) ? n.attrs : {};
    const start = Number.isInteger(attrs.start) && (attrs.start as number) >= 1 ? (attrs.start as number) : 1;
    return { type: 'orderedList', attrs: { start: Math.min(start, 9999) }, content: items };
  }

  block(n: unknown, path: Array<string | number>): Block | null {
    if (!isObj(n)) return this.fail(path, 'content_invalid');
    switch (n.type) {
      case 'paragraph':
        return this.count(path) ? this.paragraph(n, path) : null;
      case 'heading': {
        if (!this.count(path)) return null;
        const attrs = isObj(n.attrs) ? n.attrs : {};
        const level = attrs.level === 3 ? 3 : attrs.level === 4 ? 4 : 2;
        const content = this.inline(n.content, [...path, 'content']);
        return content
          ? { type: 'heading', attrs: { level }, content }
          : { type: 'heading', attrs: { level } };
      }
      case 'bulletList':
      case 'orderedList':
        return this.list(n, path, 1);
      case 'blockquote': {
        if (!this.count(path)) return null;
        const kids = Array.isArray(n.content) ? n.content : [];
        const content: Paragraph[] = [];
        kids.forEach((k, i) => {
          const kp = [...path, 'content', i];
          if (!isObj(k) || k.type !== 'paragraph') return void this.fail(kp, 'content_unknown_node');
          if (this.count(kp)) content.push(this.paragraph(k, kp));
        });
        return { type: 'blockquote', content: content.length ? content : [{ type: 'paragraph' }] };
      }
      case 'horizontalRule':
        return this.count(path) ? { type: 'horizontalRule' } : null;
      case 'image': {
        if (!this.count(path)) return null;
        const attrs = isObj(n.attrs) ? n.attrs : {};
        if (typeof attrs.fileId !== 'string' || !UUID_RE.test(attrs.fileId))
          return this.fail([...path, 'attrs', 'fileId'], 'content_invalid');
        const alt = typeof attrs.alt === 'string' ? attrs.alt.replace(CONTROL, '').trim() : '';
        if (!alt) return this.fail([...path, 'attrs', 'alt'], 'content_image_alt_required');
        const caption =
          typeof attrs.caption === 'string' && attrs.caption.trim()
            ? attrs.caption.replace(CONTROL, '').trim().slice(0, LIMITS.caption)
            : null;
        return {
          type: 'image',
          attrs: { fileId: attrs.fileId.toLowerCase(), alt: alt.slice(0, LIMITS.alt), caption },
        };
      }
      case 'table':
        return this.table(n, path);
      case 'video': {
        if (!this.count(path)) return null;
        const attrs = isObj(n.attrs) ? n.attrs : {};
        const provider = attrs.provider === 'youtube' || attrs.provider === 'vimeo' ? attrs.provider : null;
        if (!provider || typeof attrs.videoId !== 'string' || !isVideoId(provider, attrs.videoId))
          return this.fail([...path, 'attrs'], 'content_invalid');
        const title = typeof attrs.title === 'string' ? attrs.title.replace(CONTROL, '').trim() : '';
        if (!title) return this.fail([...path, 'attrs', 'title'], 'content_video_title_required');
        return {
          type: 'video',
          attrs: { provider, videoId: attrs.videoId, title: title.slice(0, LIMITS.videoTitle) },
        };
      }
      default:
        return this.fail(path, 'content_unknown_node');
    }
  }

  /** Tablo: en çok 100 satır × 12 sütun; hücrede yalnızca paragraf (birleştirme öznitelikleri atılır). */
  table(n: Obj, path: Array<string | number>): TableNode | null {
    if (!this.count(path)) return null;
    const rows = Array.isArray(n.content) ? n.content : [];
    if (!rows.length) return this.fail(path, 'content_invalid');
    if (rows.length > LIMITS.tableRows) return this.fail(path, 'content_too_large');
    const out: TableRow[] = [];
    rows.forEach((r, i) => {
      const rp = [...path, 'content', i];
      if (!isObj(r) || r.type !== 'tableRow') return void this.fail(rp, 'content_unknown_node');
      if (!this.count(rp)) return;
      const cells = Array.isArray(r.content) ? r.content : [];
      if (!cells.length) return void this.fail(rp, 'content_invalid');
      if (cells.length > LIMITS.tableColumns) return void this.fail(rp, 'content_too_large');
      const row: TableCell[] = [];
      cells.forEach((c, j) => {
        const cp = [...rp, 'content', j];
        if (!isObj(c) || (c.type !== 'tableCell' && c.type !== 'tableHeader'))
          return void this.fail(cp, 'content_unknown_node');
        if (!this.count(cp)) return;
        const kids = Array.isArray(c.content) ? c.content : [];
        const content: Paragraph[] = [];
        kids.forEach((k, m) => {
          const kp = [...cp, 'content', m];
          if (!isObj(k) || k.type !== 'paragraph') return void this.fail(kp, 'content_unknown_node');
          if (this.count(kp)) content.push(this.paragraph(k, kp));
        });
        row.push({ type: c.type, content: content.length ? content : [{ type: 'paragraph' }] });
      });
      out.push({ type: 'tableRow', content: row });
    });
    return { type: 'table', content: out };
  }
}

export type ParseResult = { ok: true; doc: RichDoc } | { ok: false; issues: ContentIssue[] };

/** Belgeyi doğrular ve kanonik biçime getirir (bilinmeyen öznitelikler atılır, boş metinler çıkarılır). */
export function safeParseRichDoc(raw: unknown): ParseResult {
  if (!isObj(raw) || raw.type !== 'doc' || !Array.isArray(raw.content))
    return { ok: false, issues: [{ path: [], code: 'content_invalid' }] };
  const w = new Walker();
  const content: Block[] = [];
  raw.content.forEach((n, i) => {
    const b = w.block(n, ['content', i]);
    if (b) content.push(b);
  });
  if (w.issues.length) return { ok: false, issues: w.issues };
  return { ok: true, doc: { type: 'doc', content } };
}

export function parseRichDoc(raw: unknown): RichDoc {
  const r = safeParseRichDoc(raw);
  if (!r.ok) throw new ContentError(r.issues);
  return r.doc;
}

export function emptyDoc(): RichDoc {
  return { type: 'doc', content: [] };
}

/** Düz metinden belge (paragraflar boş satırla ayrılır). İçe aktarma ve testler için. */
export function docFromPlainText(text: string): RichDoc {
  const paragraphs = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parseRichDoc({
    type: 'doc',
    content: paragraphs.map((p) => ({
      type: 'paragraph',
      content: p
        .split('\n')
        .flatMap((line, i) => [...(i > 0 ? [{ type: 'hardBreak' }] : []), { type: 'text', text: line }]),
    })),
  });
}

/** Belgede kullanılan görsel dosya kimlikleri (yayında görünürlük ve silme denetimi için). */
export function imageFileIds(doc: RichDoc): string[] {
  return doc.content.flatMap((b) => (b.type === 'image' ? [b.attrs.fileId] : []));
}
